import React, { useEffect, useRef, useState } from 'react'
import { Dimensions, Modal, Platform, ScrollView, TextStyle, View, ViewStyle } from 'react-native'
import LinearGradient from 'react-native-linear-gradient'
import { Check, Close, Cancel, CheckCircle, Tour } from '@mui/icons-material'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { observer } from 'mobx-react-lite'
import { Text as RNText } from 'react-native'

import { color } from '../../theme'
import { iconFlag } from '../../assets'
import { Text } from '../text'
import { SvgIcon } from '../svg-icon'
import { Button } from '../button'
import { TouchableOpacity } from 'react-native-gesture-handler'
import { useStores } from '@trivie/core'
import { TextInput } from '../text-input'
const _sample = require('lodash/sample')

interface FeedbackModalProps {
  selection: any
  question: any
  branding: any
  onContinue: () => void
  feedbackRef: any
  open?: boolean
  correct?: boolean
  visible?: boolean
  showChoiceFeedback?: boolean
}

const { height } = Dimensions.get('window')

export const FeedbackModal = observer((props: FeedbackModalProps) => {
  const { userStore } = useStores()
  const { user } = userStore
  const {
    selection,
    showChoiceFeedback,
    question,
    visible,
    branding,
    correct,
    feedbackRef,
    onContinue,
  } = props

  const [longFactoid, setLongFactoid] = useState(false)
  const [closeToBottom, setCloseToBottom] = useState(false)
  const [closeToTop, setCloseToTop] = useState(true)
  const [label, setLabel] = useState('Correct!')

  const primary = correct ? branding.correct_answer : branding.incorrect_answer

  useEffect(() => {
    const correctOptions = [
      "That's right!",
      'Great work!',
      'Perfect!',
      'Spot on!',
      'Nice job!',
      'Nicely done!',
      'Terrific!',
    ]
    const incorrectOptions = ['Not quite', "Oops - that's not correct"]
    let status = correct ? 'Correct!' : 'Incorrect'

    if (user?.language === 'en' && correct && Math.random() > 0.5) {
      status = _sample(correctOptions)
    } else if (user?.language === 'en' && !correct && Math.random() > 0.7) {
      status = _sample(incorrectOptions)
    }
    setLabel(status)
  }, [correct, selection])

  const handleContinue = () => {
    feedbackRef?.current?.close()
    onContinue()
  }

  const onScroll = ({ nativeEvent }) => {
    const { layoutMeasurement, contentOffset, contentSize } = nativeEvent
    setCloseToBottom(layoutMeasurement.height + contentOffset.y >= contentSize.height)
    setCloseToTop(contentOffset.y <= 0)
  }

  const onTextLayout = ({ nativeEvent }) => {
    const { layout } = nativeEvent
    if (layout.height / height > 0.73) {
      setLongFactoid(true)
    } else {
      setLongFactoid(false)
    }
  }

  const renderFooter = () => (
    <>
      {longFactoid && !closeToBottom && (
        <LinearGradient
          style={{ ...GRADIENT, bottom: 88 }}
          colors={[color.transparent, branding.feedback_background]}
        />
      )}
      <View style={{ ...NEXT_BTN_CONTAINER, backgroundColor: branding.feedback_background }}>
        <Button
          variant="contained"
          style={{ ...NEXT_BTN, backgroundColor: primary }}
          textStyle={{ color: color.white }}
          tx="Common.next"
          onPress={handleContinue}
        />
      </View>
    </>
  )

  const renderHandle = () => (
    <>
      <View style={{ ...HANDLE }}>
        <View style={HEADER}>
          <View style={STATUS}>
            {correct ? <CheckCircle htmlColor={primary} /> : <Cancel htmlColor={primary} />}
            <Text style={{ color: primary }} text={label} variant="labelEmphasized" />
          </View>
        </View>
      </View>
    </>
  )

  const renderContent = () => {
    const mca = question?.question_type === 'Multiple Response'
    const factoid =
      showChoiceFeedback && selection?.length && !mca
        ? selection[0]?.factoid?.text
        : question.factoid?.text

    const mcaFactoid = (
      <View onLayout={onTextLayout} style={{ gap: 8, display: 'flex', flexDirection: 'column' }}>
        {question.choices.map((c) => {
          const included = selection.some((s) => s.id === c.id)
          return (
            <View style={{ gap: 10, display: 'flex', flexDirection: 'row' }}>
              {(c.correct && included) || (!c.correct && !included) ? (
                <Check htmlColor={branding.feedback_text} />
              ) : (
                <Close htmlColor={branding.feedback_text} />
              )}
              <Text
                disableLink
                style={{ flex: 1, color: branding.feedback_text }}
                text={c.factoid?.text}
                variant={'labelMedium'}
              />
            </View>
          )
        })}
      </View>
    )
    const incorrectOrdering = (
      <View style={{ marginBottom: 20 }}>
        <Text
          disableLink
          variant="title"
          style={{ color: branding.feedback_text, marginBottom: 20 }}
          text="The correct order is:"
        />
        <View style={{ alignItems: 'center', flexDirection: 'row', gap: 16 }}>
          <View
            style={{
              marginBottom: 0,
              flexDirection: 'column',
              alignSelf: 'flex-start',
              gap: 8,
              flex: 1,
              alignItems: 'flex-start',
            }}
          >
            {question.choices[0].ordering?.map((o, idx) => {
              return (
                <View style={{ gap: 8, flex: 1, flexDirection: 'row', display: 'flex' }}>
                  <View>
                    <Text
                      variant="title"
                      disableLink
                      style={{ width: 20, color: branding!.feedback_text }}
                      text={`${idx + 1}.`}
                    />
                  </View>
                  <Text
                    disableLink
                    variant="title"
                    style={{ color: branding!.feedback_text }}
                    text={o.text}
                  />
                </View>
              )
            })}
          </View>
        </View>
      </View>
    )
    const incorrectMatching = (
      <View style={{ marginBottom: 20 }}>
        <Text
          disableLink
          variant="title"
          style={{ color: branding!.feedback_text, marginBottom: 20, paddingTop: 10 }}
          text="The correct matching is:"
        />
        <View style={{ alignItems: 'center', flexDirection: 'row', gap: 16 }}>
          <View
            style={{
              alignItems: 'flex-start',
              marginBottom: 0,
              flexDirection: 'column',
              alignSelf: 'flex-start',
              gap: 8,
              flex: 1,
            }}
          >
            {question.choices[0].pairs?.map((pair, idx) => {
              return (
                <View style={{ gap: 8, flex: 1, flexDirection: 'row', display: 'flex' }}>
                  <View>
                    <Text
                      variant="title"
                      disableLink
                      style={{ width: 20, color: branding!.feedback_text }}
                      text={`${idx + 1}.`}
                    />
                  </View>
                  <Text
                    disableLink
                    variant="title"
                    style={{ color: branding!.feedback_text }}
                    text={`${pair.left} → ${pair.right}`}
                  />
                </View>
              )
            })}
          </View>
        </View>
      </View>
    )

    const incorrectOpenText = (
      <View style={{ marginBottom: 20, gap: 20 }}>
        {!factoid && <View style={{ flex: 1, marginBottom: 20 }} />}
        <View style={{ flexDirection: 'row' }}>
          <RNText
            style={{
              fontStyle: 'normal',
              fontWeight: '500',
              fontSize: 17,
              lineHeight: 22,
              letterSpacing: -0.2,
              color: branding!.feedback_text,
            }}
          >
            Your Answer:&nbsp;
            <Text
              disableLink
              variant="labelMedium"
              style={{ color: branding!.feedback_text, textDecorationLine: 'underline' }}
              text={question.userText}
            />
          </RNText>
        </View>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap' }}>
          <RNText
            style={{
              fontStyle: 'normal',
              fontWeight: '500',
              fontSize: 17,
              lineHeight: 22,
              letterSpacing: -0.2,
              color: branding!.feedback_text,
            }}
          >
            {`Correct Answer${question.correctChoices?.length === 1 ? '' : 's'}:`}&nbsp;
            <Text
              disableLink
              variant="labelMedium"
              style={{ color: branding!.feedback_text }}
              text={`${question.correctChoices.map((c) => c.text).join(', ')}`}
            />
          </RNText>
        </View>
      </View>
    )

    return (
      <>
        {longFactoid && !closeToTop && (
          <LinearGradient
            style={{ ...GRADIENT, top: 0 }}
            colors={[branding.feedback_background, color.transparent]}
          />
        )}
        <ScrollView onScroll={onScroll} contentContainerStyle={{ paddingHorizontal: 24 }}>
          {mca && showChoiceFeedback ? (
            mcaFactoid
          ) : (
            <>
              {!question?.answeredCorrectly && question.question_type === 'Matching'
                ? incorrectMatching
                : null}
              {!question?.answeredCorrectly && question.question_type === 'Ordering'
                ? incorrectOrdering
                : null}
              {!question?.answeredCorrectly && question.question_type === 'Open Text'
                ? incorrectOpenText
                : null}
              <Text
                onLayout={onTextLayout}
                style={{ color: branding.feedback_text }}
                text={factoid}
                variant="labelMedium"
              />
            </>
          )}
        </ScrollView>
      </>
    )
  }

  return (
    <>
      {visible && (
        <Modal
          hardwareAccelerated
          animationType="fade"
          visible={visible}
          transparent
          presentationStyle="fullScreen"
        >
          <View style={CONTAINER}>
            <View
              style={{
                ...MODAL,
                backgroundColor: branding.feedback_background,
                borderColor: primary,
              }}
            >
              <View
                style={{
                  marginBottom: question.factoid || showChoiceFeedback ? 24 : 0,
                  paddingHorizontal: 24,
                }}
              >
                {renderHandle()}
              </View>
              <View style={{ ...CONTENT, marginBottom: 24 }}>{renderContent()}</View>
              <View style={FOOTER}>{renderFooter()}</View>
            </View>
          </View>
        </Modal>
      )}
    </>
  )
})

const CONTAINER: ViewStyle = {
  height: '100%',
  justifyContent: 'center',
  backgroundColor: 'rgba(0,0,0,0.8)',
  paddingLeft: 30,
  paddingRight: 30,
}
const FOOTER: ViewStyle = { paddingHorizontal: 24 }
const MODAL: ViewStyle = {
  borderRadius: 24,
  borderWidth: 1,
  height: 'auto',
  maxHeight: window.innerHeight - 80,
  width: '100%',
  maxWidth: 824,
  alignSelf: 'center',
  position: 'relative',
  paddingTop: 24,
  paddingBottom: 24,
}
const HANDLE: ViewStyle = {}
const CONTENT: ViewStyle = { flex: 1, justifyContent: 'center', position: 'relative' }
const NEXT_BTN_CONTAINER: ViewStyle = {
  borderBottomLeftRadius: 24,
  borderBottomRightRadius: 24,
  width: '100%',
}
const REPORT_BTN_CONTAINER: ViewStyle = {
  ...NEXT_BTN_CONTAINER,
  flexDirection: 'row',
  gap: 20,
}
const NEXT_BTN: ViewStyle = { zIndex: 9999, borderRadius: 24, height: 52 }
const REPORT_BTN: ViewStyle = {
  flex: 1,
  zIndex: 9999,
  borderRadius: 10,
  height: 36,
}
const CANCEL_BTN: ViewStyle = {
  ...REPORT_BTN,
  backgroundColor: color.transparent,
  borderWidth: 1,
  borderColor: '#FFFFFF20',
}
const HEADER: ViewStyle = {
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
}
const STATUS: ViewStyle = { flexDirection: 'row', alignItems: 'center', gap: 10 }
const GRADIENT: ViewStyle = {
  position: 'absolute',
  left: 0,
  width: '100%',
  height: 60,
  zIndex: 9999,
}
const REASON_BTN: ViewStyle = {
  height: 30,
  borderRadius: 100,
  paddingHorizontal: 12,
  paddingVertical: 6,
  borderWidth: 1,
  borderColor: color.blue,
}
const REPORT_REASON_INPUT: TextStyle = {
  borderWidth: 1,
  borderColor: '#ffffff20',
  borderRadius: 12,
  color: color.white,
  fontWeight: '400',
  fontFamily: 'Inter',
  fontSize: 14,
  paddingTop: 8,
  paddingHorizontal: 12,
  paddingBottom: 24,
  minHeight: 130,
}
const CHARACTER_COUNT: TextStyle = { position: 'absolute', bottom: 8, right: 12 }
